import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Send,
  BookOpen,
  Paperclip,
  X,
  ArrowLeft,
} from "lucide-react";
import { SectionLabel } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { getThread, sendChatMessage, type ThreadMessage } from "@/api/chat";

export default function ChatThread() {
  const { threadId: threadIdParam } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();

  const isNew = threadIdParam === "new";
  const numericThreadId = Number(threadIdParam);
  const threadIdValid =
    !isNew && Number.isFinite(numericThreadId) && numericThreadId > 0;

  const threadQ = useQuery({
    queryKey: ["chat-thread", numericThreadId],
    queryFn: () => getThread(numericThreadId),
    enabled: threadIdValid,
  });

  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [knowledgeByMessage, setKnowledgeByMessage] = useState<
    Record<number, Array<{ id: number; title: string; category: string }>>
  >({});

  useEffect(() => {
    if (threadQ.data) {
      setMessages(threadQ.data.messages);
    }
  }, [threadQ.data]);

  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, sending]);

  // Paste-from-clipboard image support
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const imageFiles: File[] = [];
      for (const item of items) {
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const f = item.getAsFile();
          if (f) imageFiles.push(f);
        }
      }
      if (imageFiles.length > 0) {
        e.preventDefault();
        setAttachments((prev) => [...prev, ...imageFiles].slice(0, 5));
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);

  const addFiles = useCallback((fl: FileList | null) => {
    if (!fl) return;
    const valid = Array.from(fl).filter((f) => f.type.startsWith("image/"));
    setAttachments((prev) => [...prev, ...valid].slice(0, 5));
  }, []);

  const removeAttachment = (i: number) => {
    setAttachments((prev) => prev.filter((_, idx) => idx !== i));
  };

  const send = async (e?: FormEvent) => {
    if (e) e.preventDefault();
    if (sending) return;
    const textToSend = text.trim();
    const filesToSend = [...attachments];
    if (!textToSend && filesToSend.length === 0) return;

    // Phase28.1: clear input IMMEDIATELY so the user can start typing the next
    // message while the coach is generating. The captured locals above are
    // what we actually send; the optimistic user bubble is rendered while the
    // request is in flight.
    setText("");
    setAttachments([]);
    if (fileRef.current) fileRef.current.value = "";
    if (taRef.current) taRef.current.style.height = "96px";

    // Optimistic local user message (replaced when server response arrives)
    const optimisticId = -Date.now();
    setMessages((prev) => [
      ...prev,
      {
        id: optimisticId,
        role: "user",
        content: textToSend || "(фото)",
        created_at: new Date().toISOString(),
        attachments: [],
      },
    ]);

    setSending(true);
    try {
      const res = await sendChatMessage(textToSend || "(фото)", {
        threadId: threadIdValid ? numericThreadId : null,
        files: filesToSend,
      });
      setMessages((prev) => [
        // Drop the optimistic placeholder, then append server's authoritative pair
        ...prev.filter((m) => m.id !== optimisticId),
        res.user_message,
        res.assistant_message,
      ]);
      if (res.knowledge_created && res.knowledge_created.length > 0) {
        setKnowledgeByMessage((prev) => ({
          ...prev,
          [res.assistant_message.id]: res.knowledge_created,
        }));
      }

      // If this was a new thread, navigate into it so deep-link works
      if (isNew) {
        qc.invalidateQueries({ queryKey: ["chat-threads"] });
        nav(`/chat/${res.thread.id}`, { replace: true });
      } else {
        qc.invalidateQueries({ queryKey: ["chat-threads"] });
        qc.invalidateQueries({ queryKey: ["chat-thread", numericThreadId] });
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev.filter((m) => m.id !== optimisticId),
        {
          id: -Date.now(),
          role: "assistant",
          content: "Не удалось получить ответ от тренера. Попробуй ещё раз чуть позже.",
          created_at: new Date().toISOString(),
          attachments: [],
        },
      ]);
      // Restore the user's text so they can retry without re-typing.
      setText(textToSend);
      setAttachments(filesToSend);
    } finally {
      setSending(false);
    }
  };

  const title = threadQ.data?.title ?? (isNew ? "Новый диалог" : "Загрузка…");

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-7xl flex-col">
      <header className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Link
            to="/chat"
            className="inline-flex items-center gap-1 text-[12px] text-taupe hover:text-navy-deep"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.8} />
            Все разговоры
          </Link>
          <h1 className="mt-1 truncate font-serif text-[22px] font-medium leading-snug md:text-[26px]">
            {title}
          </h1>
          <p className="text-[12px] text-taupe">
            AI · контекст 20 сообщений + База знаний + текущее состояние
          </p>
        </div>
      </header>

      <div className="flex-1 space-y-4 overflow-y-auto pb-4">
        {messages.length === 0 && !threadQ.isLoading ? (
          <div className="rounded-2xl border border-dashed border-cream-border bg-cream-light p-5 text-center text-[13px] text-taupe">
            Напиши первое сообщение или прикрепи фото (витамины, сыпь, результаты теста).
          </div>
        ) : null}

        {messages.map((m) =>
          m.role === "user" ? (
            <UserMessage key={m.id} msg={m} />
          ) : (
            <CoachMessage
              key={m.id}
              msg={m}
              knowledgeCreated={knowledgeByMessage[m.id]}
            />
          ),
        )}

        {sending ? (
          <div className="mr-auto flex max-w-[85%] items-center gap-3 rounded-2xl bg-navy px-5 py-3 text-cream-light">
            <span className="flex gap-1">
              <span
                className="h-2 w-2 animate-bounce rounded-full bg-amber"
                style={{ animationDelay: "0ms" }}
              />
              <span
                className="h-2 w-2 animate-bounce rounded-full bg-amber"
                style={{ animationDelay: "150ms" }}
              />
              <span
                className="h-2 w-2 animate-bounce rounded-full bg-amber"
                style={{ animationDelay: "300ms" }}
              />
            </span>
            <span className="text-[13px] italic text-cream-light">
              тренер ищет информацию и думает…
            </span>
          </div>
        ) : null}
        <div ref={endRef} />
      </div>

      {/* Composer */}
      <form
        onSubmit={send}
        className="sticky bottom-24 z-10 rounded-2xl border border-cream-border bg-cream-light/95 p-3 shadow-soft backdrop-blur md:bottom-3"
      >
        {attachments.length > 0 ? (
          <div className="mb-2 flex gap-2 overflow-x-auto">
            {attachments.map((f, i) => (
              <div key={i} className="relative shrink-0">
                <img
                  src={URL.createObjectURL(f)}
                  alt=""
                  className="h-16 w-16 rounded-md border border-cream-border object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeAttachment(i)}
                  className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-navy text-[11px] text-cream-light hover:bg-navy-deep"
                >
                  <X className="h-3 w-3" strokeWidth={2.5} />
                </button>
              </div>
            ))}
          </div>
        ) : null}

        <div className="flex items-end gap-3">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            title="Прикрепить фото"
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-cream-border bg-cream-light text-taupe hover:bg-cream hover:text-navy-deep"
          >
            <Paperclip className="h-5 w-5" strokeWidth={1.8} />
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => addFiles(e.target.files)}
            className="hidden"
          />
          <div className="flex-1 rounded-xl border border-cream-border bg-cream-light">
            <textarea
              ref={taRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
              placeholder={
                attachments.length > 0
                  ? "Что спросить про эти фото?"
                  : "Напиши тренеру…"
              }
              rows={1}
              style={{ minHeight: "96px", maxHeight: "240px" }}
              className="w-full resize-none rounded-xl bg-transparent px-4 py-3 text-[14px] leading-relaxed text-navy-deep placeholder:text-taupe/70 focus:outline-none"
            />
          </div>
          <button
            type="submit"
            disabled={!text.trim() && attachments.length === 0}
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber text-navy-deep shadow-soft hover:bg-amber-dark disabled:cursor-not-allowed disabled:bg-taupe/30 disabled:text-cream-light disabled:shadow-none"
            title="Отправить"
          >
            <Send className="h-5 w-5" strokeWidth={2} />
          </button>
        </div>
        <div className="mt-2 text-center text-[11px] text-taupe">
          Enter — отправить · Shift+Enter — новая строка · фото можно вставить из буфера
        </div>
      </form>
    </div>
  );
}

function fmtTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString("ru-RU", {
      hour: "2-digit",
      minute: "2-digit",
      day: "numeric",
      month: "long",
    });
  } catch {
    return "";
  }
}

function UserMessage({ msg }: { msg: ThreadMessage }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[85%]">
        {msg.attachments && msg.attachments.length > 0 ? (
          <div className="mb-1 grid grid-cols-2 gap-1.5">
            {msg.attachments.map((a) => (
              <img
                key={a.id}
                src={`/uploads/${a.path}`}
                alt=""
                className="w-full rounded-md border border-cream-border object-cover"
                loading="lazy"
              />
            ))}
          </div>
        ) : null}
        <div className="whitespace-pre-wrap rounded-2xl bg-amber px-4 py-2.5 text-[15px] leading-relaxed text-navy-deep shadow-soft">
          {msg.content}
        </div>
        <div className="mt-1 text-right text-[11px] text-taupe">
          {fmtTime(msg.created_at)}
        </div>
      </div>
    </div>
  );
}

function CoachMessage({
  msg,
  knowledgeCreated,
}: {
  msg: ThreadMessage;
  knowledgeCreated?: Array<{ id: number; title: string; category: string }>;
}) {
  return (
    <div className="coach-markdown-block max-w-[92%] rounded-2xl bg-navy px-5 py-4 shadow-soft">
      <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.18em] text-amber-light">
        Тренер говорит
      </div>
      <div className="coach-markdown-content">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ children }) => (
              <h2 className="mb-3 font-serif text-[20px] font-medium text-cream-light">
                {children}
              </h2>
            ),
            h2: ({ children }) => (
              <h3 className="mb-2 mt-4 font-serif text-[17px] font-medium text-cream-light">
                {children}
              </h3>
            ),
            h3: ({ children }) => (
              <div className="mb-1.5 mt-3 text-[11px] font-medium uppercase tracking-[0.18em] text-amber-light">
                {children}
              </div>
            ),
            p: ({ children }) => (
              <p className="mb-3 text-[15px] leading-relaxed text-cream-light">
                {children}
              </p>
            ),
            strong: ({ children }) => (
              <span className="font-medium text-cream-light">{children}</span>
            ),
            em: ({ children }) => (
              <span className="italic text-cream-light">{children}</span>
            ),
            a: ({ href, children }) => (
              <a
                href={href}
                target="_blank"
                rel="noreferrer noopener"
                className="text-amber-light underline decoration-amber-light/50 hover:decoration-amber-light"
              >
                {children}
              </a>
            ),
            ul: ({ children }) => (
              <ul className="my-3 list-disc space-y-1.5 pl-5 marker:text-amber">
                {children}
              </ul>
            ),
            ol: ({ children }) => (
              <ol className="my-3 list-decimal space-y-1.5 pl-5 marker:text-amber-light">
                {children}
              </ol>
            ),
            li: ({ children }) => (
              <li className="pl-1 text-[15px] leading-relaxed text-cream-light">
                {children}
              </li>
            ),
            code: ({ children }) => (
              <code className="rounded bg-cream-light/10 px-1.5 py-0.5 font-mono text-[13px] text-amber-light">
                {children}
              </code>
            ),
            blockquote: ({ children }) => (
              <blockquote className="my-3 border-l-2 border-amber pl-3 text-[14px] italic text-cream-light">
                {children}
              </blockquote>
            ),
            hr: () => <hr className="my-4 border-cream-light/15" />,
            table: ({ children }) => (
              <table className="my-3 w-full border-collapse text-[13px] text-cream-light">
                {children}
              </table>
            ),
            th: ({ children }) => (
              <th className="border-b border-cream-light/20 px-2 py-1.5 text-left font-medium text-amber-light">
                {children}
              </th>
            ),
            td: ({ children }) => (
              <td className="border-b border-cream-light/10 px-2 py-1.5">
                {children}
              </td>
            ),
          }}
        >
          {msg.content}
        </ReactMarkdown>
      </div>
      {knowledgeCreated && knowledgeCreated.length > 0 ? (
        <div className="mt-3 rounded-lg border-l-2 border-amber bg-amber/15 px-3 py-2 text-[12px] text-cream-light">
          <div className="mb-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.15em] text-amber-light">
            <BookOpen className="h-3 w-3" strokeWidth={2} />
            Сохранено в базу знаний
          </div>
          <ul className="space-y-0.5">
            {knowledgeCreated.map((kb) => (
              <li key={kb.id}>
                <Link
                  to="/knowledge"
                  className="text-amber-light underline decoration-amber-light/40 hover:decoration-amber-light"
                >
                  {kb.title}
                </Link>
                <span className="ml-1.5 text-cream-light">[{kb.category}]</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="mt-3 border-t border-cream-light/10 pt-2 text-[11px] text-cream-light">
        {fmtTime(msg.created_at)}
      </div>
    </div>
  );
}
